import {
  IPlayerOption,
  ISelectionButton,
  ITeams,
  ITwoRowGenericSelectorInp,
  TAltPlayerOptions,
  TColors,
  TOffDef,
  TPassPlays,
  TPatEvents,
  TPenaltyDownResult,
  TPenaltyDriveResult,
  TPlayResults,
  TPlays,
  TRefEvents,
  TRowJustify,
  TTeam,
  TUserRole,
} from '@/types';
import { PLAY_RESULT_TYPES, TPlayResultTypes } from './gameState';
import { TIcons } from '@/constants/icons';

export const MAIN_MACHINE_ID = 'BASE_MACHINE';

export const PRIMARY_FLOW_INPUTS: ITwoRowGenericSelectorInp = {
  topJustify: 'start',
  topOptions: [
    {
      text: TPlays.PUNT,
      value: TPlays.PUNT,
      col: 4,
    },
    {
      text: TPlays.REGULAR,
      value: TPlays.REGULAR,
      col: 8,
    },
  ],
  bottomJustify: 'start',
  bottomOptions: [
    {
      text: TPlays.KICKOFF,
      value: TPlays.KICKOFF,
      col: 4,
    },
    {
      text: 'FG/PAT',
      value: TPlays.FG,
      col: 4,
    },
    {
      text: TPlays.REF,
      value: TPlays.REF,
      color: TColors.YELLOW,
      col: 4,
    },
  ],
};

export const PASS_RESULT_INPUTS: ITwoRowGenericSelectorInp = {
  topJustify: 'start',
  topOptions: [
    {
      text: TPassPlays.QB_SCRAMBLE,
      value: TPassPlays.QB_SCRAMBLE,
      col: 4,
    },
    {
      text: TPassPlays.INCOMPLETE,
      value: TPassPlays.INCOMPLETE,
      col: 4,
    },
    {
      text: TPassPlays.COMPLETE,
      value: TPassPlays.COMPLETE,
      col: 4,
    },
  ],
  bottomJustify: 'start',
  bottomOptions: [
    {
      text: TPassPlays.INTERCEPTION,
      value: TPassPlays.INTERCEPTION,
      color: TColors.RED,
      col: 4,
    },
    {
      text: TPlays.REF,
      value: TPlays.REF,
      color: TColors.YELLOW,
      col: 4,
    },
    {
      text: TPlays.PENALTY_ALERT,
      value: TPlays.PENALTY_ALERT,
      color: TColors.ORANGE,
      isToggle: true,
      col: 4,
    },
  ],
};

export const YARDAGE_SELECTOR_BUTTONS = (preTogglePenalty?: boolean): ISelectionButton[] => [
  {
    text: TPlayResults.TURNOVER,
    value: TPlayResults.TURNOVER,
    color: TColors.RED,
    isToggle: true,
  },
  {
    text: TPlayResults.CLOSE_1ST,
    value: TPlayResults.CLOSE_1ST,
    color: TColors.YELLOW,
    isToggle: true,
  },
  {
    text: TPlayResults.PENALTY,
    value: TPlayResults.PENALTY,
    color: TColors.ORANGE,
    isToggle: true,
    toggleInitialValue: !!preTogglePenalty,
  },
  {
    text: TPlayResults.NEXT,
    value: TPlayResults.NEXT,
    color: TColors.GREEN,
    img: TIcons.CHEVRON_RIGHT,
    alt: '>',
  },
];

export const YARDAGE_SELECTOR_TOGGLE_PROPRITY: { [key: string]: number } = {
  [TPlayResults.TURNOVER]: 1,
  [TPlayResults.PENALTY]: 0,
};

export const REF_EVENT_INPUTS: ITwoRowGenericSelectorInp = {
  topJustify: 'start',
  topOptions: [
    {
      text: TRefEvents.REVIEW,
      value: TRefEvents.REVIEW,
      color: TColors.YELLOW,
      col: 6,
    },
    {
      text: TRefEvents.PENALTY,
      value: TRefEvents.PENALTY,
      color: TColors.YELLOW,
      col: 6,
    },
  ],
  bottomJustify: 'start',
  bottomOptions: [
    {
      text: TRefEvents.NO_PLAY,
      value: TRefEvents.NO_PLAY,
      color: TColors.RED,
      img: TIcons.CLOSE,
      col: 4,
      alt: 'X',
    },
    {
      text: TRefEvents.CHAINS,
      value: TRefEvents.CHAINS,
      color: TColors.YELLOW,
      col: 4,
    },
    {
      text: TRefEvents.CHALLENGE,
      value: TRefEvents.CHALLENGE,
      color: TColors.YELLOW,
      col: 4,
    },
  ],
};

export const REF_EVENT_INPUTS_NO_REVIEW_CHALLENGE: ITwoRowGenericSelectorInp = {
  topJustify: 'start',
  topOptions: [
    {
      text: TRefEvents.PENALTY,
      value: TRefEvents.PENALTY,
      color: TColors.YELLOW,
      col: 12,
    },
  ],
  bottomJustify: 'start',
  bottomOptions: [
    {
      text: TRefEvents.NO_PLAY,
      value: TRefEvents.NO_PLAY,
      color: TColors.RED,
      img: TIcons.CLOSE,
      col: 6,
      alt: 'X',
    },
    {
      text: TRefEvents.CHAINS,
      value: TRefEvents.CHAINS,
      color: TColors.YELLOW,
      col: 6,
    },
  ],
};

export const PENALTY_INPUTS = (
  offenseTeam: TTeam,
  offenseTeamLogo: string,
  defenseTeam: TTeam,
  defenseTeamLogo: string
): ITwoRowGenericSelectorInp => ({
  title: 'Penalty',
  topJustify: 'start',
  topOptions: [
    {
      text: TOffDef.OFFENSE,
      value: offenseTeam,
      img: offenseTeamLogo,
      alt: 'off',
      col: 6,
    },
    {
      text: TOffDef.DEFENSE,
      value: defenseTeam,
      img: defenseTeamLogo,
      alt: 'def',
      col: 6,
    },
  ],
  bottomJustify: 'start',
  bottomOptions: [
    {
      text: TPlayResults.DECLINED,
      value: TPlayResults.DECLINED,
      color: TColors.RED,
      img: TIcons.CLOSE,
      alt: 'X',
      col: 4,
    },
    {
      text: TPlayResults.GROUNDING,
      value: TPlayResults.GROUNDING,
      col: 4,
    },
    {
      text: TOffDef.BOTH,
      value: TOffDef.BOTH,
      col: 4,
    },
  ],
});

export const TOV_INPUT = (hideInterception = false): ITwoRowGenericSelectorInp => {
  const inp = {
    title: 'Turnover Type',
    topJustify: hideInterception ? 'end' : ('start' as TRowJustify),
    topOptions: [
      {
        text: PLAY_RESULT_TYPES[TPlayResultTypes.FUMBLE_LOST],
        value: TPlayResultTypes.FUMBLE_LOST,
        color: TColors.RED,
        col: 6,
      },
    ],
    bottomJustify: 'end' as TRowJustify,
    bottomOptions: [
      {
        text: 'NO TURNOVER',
        value: '',
        col: 4,
      },
    ],
  };

  if (!hideInterception) {
    inp.topOptions.push({
      text: PLAY_RESULT_TYPES[TPlayResultTypes.INTERCEPTION],
      value: TPlayResultTypes.INTERCEPTION,
      color: TColors.RED,
      col: 6,
    });
  }

  return inp;
};

export const KICKOFF_INPUTS = (backTo?: string): ITwoRowGenericSelectorInp => ({
  topJustify: 'start',
  topOptions: [
    {
      text: 'ONSIDE KICK FORMATION',
      value: TPlays.ONSIDE,
      col: 4,
    },
    {
      text: 'REGULAR FORMATION',
      value: TPlays.KICKOFF,
      col: 8,
    },
  ],
  bottomJustify: 'end',
  bottomOptions: [
    {
      text: TPlays.REF,
      value: TPlays.REF,
      color: TColors.YELLOW,
      col: 4,
    },
  ],
  backTo,
});

export const PLAYER_ATTRIBUTION_INPUT = (
  rushers: IPlayerOption[],
  receivers: IPlayerOption[],
  backTo: string
): ITwoRowGenericSelectorInp => {
  const rushersForButtons = rushers.length >= 3 ? [rushers[0], rushers[2], rushers[1]] : rushers;
  const receiversForButtons = [...receivers].reverse();

  return {
    title: 'Player Attribution',
    topJustify: 'end',
    topOptions: rushersForButtons.map((p) => ({
      text: p.name,
      bigText: p.number,
      value: p.playerId,
      col: 4,
    })),
    bottomJustify: 'end',
    bottomOptions: receiversForButtons.map((p) => ({
      text: p.name,
      bigText: p.number,
      value: p.playerId,
      col: 4,
    })),
    titleBarAction: {
      text: TAltPlayerOptions.OTHER,
      value: null,
    },
    backTo,
  };
};

export const UNDER_REVIEW_INPUTS: ITwoRowGenericSelectorInp = {
  topJustify: 'start',
  topOptions: [
    {
      text: TPlayResults.OVERTURNED,
      value: TPlayResults.OVERTURNED,
      col: 6,
    },
    {
      text: TPlayResults.CONFIRMED,
      value: TPlayResults.CONFIRMED,
      col: 6,
    },
  ],
};

export const CHAINS_INPUTS: ITwoRowGenericSelectorInp = {
  topJustify: 'start',
  topOptions: [
    {
      text: TPlayResults.SHORT,
      value: TPlayResults.SHORT,
      col: 6,
    },
    {
      text: '1ST DOWN',
      value: TPlayResults.FIRST_DOWN,
      col: 6,
    },
  ],
};

export const CHALLENGE_INPUTS = (teams: ITeams): ITwoRowGenericSelectorInp => ({
  topJustify: 'start',
  topOptions: [
    {
      text: teams.home.abbreviation,
      value: TTeam.HOME,
      img: teams.home.logoUrl,
      alt: 'home',
      col: 6,
    },
    {
      text: teams.away.abbreviation,
      value: TTeam.AWAY,
      img: teams.away.logoUrl,
      alt: 'away',
      col: 6,
    },
  ],
});

export const PUNT_RESULT_INPUTS: ITwoRowGenericSelectorInp = {
  topJustify: 'start',
  topOptions: [
    {
      text: TPlayResults.TOUCHBACK,
      value: TPlayResults.TOUCHBACK,
      col: 4,
    },
    {
      text: TPlayResults.FAIR_CATCH_OR_DOWNED,
      value: TPlayResults.FAIR_CATCH_OR_DOWNED,
      col: 4,
    },
    {
      text: TPlayResults.PUNT_RETURN,
      value: TPlayResults.PUNT_RETURN,
      col: 4,
    },
  ],
  bottomJustify: 'space-between',
  bottomOptions: [
    {
      text: TPlays.FUMBLE,
      value: TPlays.FUMBLE,
      color: TColors.RED,
      col: 4,
    },
    {
      text: TPlays.PENALTY_ALERT,
      value: TPlays.PENALTY_ALERT,
      color: TColors.ORANGE,
      isToggle: true,
      col: 4,
    },
  ],
};

export const SELECT_DOWN_INPUTS: ITwoRowGenericSelectorInp = {
  topJustify: 'start',
  topOptions: [
    {
      text: TPenaltyDownResult.REPLAY_DOWN,
      value: TPenaltyDownResult.REPLAY_DOWN,
      col: 6,
    },
    {
      text: TPenaltyDownResult.FIRST_DOWN,
      value: TPenaltyDownResult.FIRST_DOWN,
      col: 6,
    },
  ],
};

export const SELECT_DRIVE_INPUTS: ITwoRowGenericSelectorInp = {
  topJustify: 'start',
  topOptions: [
    {
      text: TPenaltyDriveResult.CONTINUE,
      value: TPenaltyDriveResult.CONTINUE,
      col: 6,
    },
    {
      text: TPenaltyDriveResult.NEW,
      value: TPenaltyDriveResult.NEW,
      col: 6,
    },
  ],
};

export const POSSESSION_INPUTS = (teams: ITeams): ITwoRowGenericSelectorInp => ({
  topJustify: 'start',
  topOptions: [
    {
      text: teams.home.abbreviation,
      value: TTeam.HOME,
      img: teams.home.logoUrl,
      alt: 'home',
      col: 6,
    },
    {
      text: teams.away.abbreviation,
      value: TTeam.AWAY,
      img: teams.away.logoUrl,
      alt: 'away',
      col: 6,
    },
  ],
});

export const REGULAR_PLAY_INPUTS = (backTo?: string): ITwoRowGenericSelectorInp => ({
  topJustify: 'start',
  topOptions: [
    {
      text: TPlays.RUSH,
      value: TPlays.RUSH,
      col: 6,
    },
    {
      text: TPlays.PASS,
      value: TPlays.PASS,
      col: 6,
    },
  ],
  bottomJustify: 'start',
  bottomOptions: [
    {
      text: TPlays.FUMBLE,
      value: TPlays.FUMBLE,
      color: TColors.RED,
      col: 6,
    },
    {
      text: TPlays.REF,
      value: TPlays.REF,
      color: TColors.YELLOW,
      col: 6,
    },
  ],
  backTo,
});

export const FIELD_GOAL_RESULT_INPUTS: ITwoRowGenericSelectorInp = {
  topJustify: 'start',
  topOptions: [
    {
      text: TPlayResults.FG_RETURN,
      value: TPlayResults.FG_RETURN,
      col: 4,
    },
    {
      text: TPlayResults.FG_MISS,
      value: TPlayResults.FG_MISS,
      col: 4,
    },
    {
      text: TPlayResults.FG_MAKE,
      value: TPlayResults.FG_MAKE,
      col: 4,
    },
  ],
  bottomJustify: 'space-between',
  bottomOptions: [
    {
      text: TPlays.FUMBLE,
      value: TPlays.FUMBLE,
      color: TColors.RED,
      col: 5,
    },
  ],
};

export const FIELD_GOAL_INPUTS: ITwoRowGenericSelectorInp = {
  topJustify: 'start',
  topOptions: [
    {
      text: TPlays.FAKE_FG,
      value: TPlays.FAKE_FG,
      col: 4,
    },
    {
      text: TPlays.FG,
      value: TPlays.FG,
      col: 8,
    },
  ],
  bottomJustify: 'start',
  bottomOptions: [
    {
      text: TPlays.FUMBLE,
      value: TPlays.FUMBLE,
      color: TColors.RED,
      col: 4,
    },
    {
      text: TPlays.PAT,
      value: TPlays.PAT,
      color: TColors.INDIGO,
      col: 4,
    },
    {
      text: TPlays.REF,
      value: TPlays.REF,
      color: TColors.YELLOW,
      col: 4,
    },
  ],
};

export const FUMBLE_INPUTS = (backTo?: string): ITwoRowGenericSelectorInp => ({
  topJustify: 'start',
  topOptions: [
    {
      text: TPlayResults.FUMBLE_RECOVERED,
      value: TPlayResults.FUMBLE_RECOVERED,
      col: 6,
    },
    {
      text: TPlayResults.FUMBLE_LOST,
      value: TPlayResults.FUMBLE_LOST,
      col: 6,
    },
  ],
  bottomJustify: 'space-between',
  bottomOptions: [
    {
      text: TPlayResults.NO_FUMBLE,
      value: TPlayResults.NO_FUMBLE,
      col: 5,
    },
    {
      text: TPlays.PENALTY_ALERT,
      value: TPlays.PENALTY_ALERT,
      color: TColors.ORANGE,
      isToggle: true,
      col: 5,
    },
  ],
  backTo,
});

export const PUNT_INPUTS: ITwoRowGenericSelectorInp = {
  topJustify: 'start',
  topOptions: [
    {
      text: TPlays.FAKE_PUNT,
      value: TPlays.FAKE_PUNT,
      col: 4,
    },
    {
      text: TPlays.PUNT,
      value: TPlays.PUNT,
      col: 8,
    },
  ],
  bottomJustify: 'space-between',
  bottomOptions: [
    {
      text: TPlays.FUMBLE,
      value: TPlays.FUMBLE,
      color: TColors.RED,
      col: 4,
    },
    {
      text: TPlays.REF,
      value: TPlays.REF,
      color: TColors.YELLOW,
      col: 4,
    },
  ],
};

export const PAT_INPUTS: ITwoRowGenericSelectorInp = {
  topJustify: 'start',
  topOptions: [
    {
      text: TPatEvents.TWO_PT_ATTEMPT,
      value: TPatEvents.TWO_PT_ATTEMPT,
      color: TColors.INDIGO,
      col: 6,
    },
    {
      text: TPatEvents.XP_ATTEMPT,
      value: TPatEvents.XP_ATTEMPT,
      color: TColors.INDIGO,
      col: 6,
    },
  ],
  bottomOptions: [
    {
      text: TPlays.REF,
      value: TPlays.REF,
      color: TColors.YELLOW,
      col: 4,
    },
  ],
  bottomJustify: 'end',
};

export const USER_ROLE_INPUTS: ITwoRowGenericSelectorInp = {
  topJustify: 'start',
  topOptions: [
    {
      text: TUserRole.CLOCK,
      value: TUserRole.CLOCK,
      col: 6,
    },
    {
      text: TUserRole.PLAYS,
      value: TUserRole.PLAYS,
      col: 6,
    },
  ],
};

export const TWO_PT_TOGGLE_BTN: ISelectionButton = {
  text: TPlayResults.TWO_PT_CONVERSION,
  value: TPlayResults.TWO_PT_CONVERSION,
  color: TColors.BLUE,
  isHorizontal: true,
  isToggle: true,
  toggleInitialValue: true,
};
export const FG_TOGGLE_BTN = (isMake: boolean): ISelectionButton => ({
  text: TPlayResults.FG_MAKE,
  value: TPlayResults.FG_MAKE,
  color: TColors.BLUE,
  isHorizontal: true,
  isToggle: true,
  toggleInitialValue: isMake,
});

export const PAT_TOGGLE_BTN: ISelectionButton = {
  text: TPlayResults.EXTRA_POINT,
  value: TPlayResults.EXTRA_POINT,
  color: TColors.BLUE,
  isHorizontal: true,
  isToggle: true,
  toggleInitialValue: true,
};

export const SAFTEY_BTN: ISelectionButton = {
  color: TColors.BLUE,
  text: TPlayResults.SAFTEY,
  value: TPlayResults.SAFTEY,
  isHorizontal: true,
  isToggle: true,
  toggleInitialValue: true,
};

export const TOUCHDOWN_BTN: ISelectionButton = {
  color: TColors.BLUE,
  text: TPlayResults.TOUCHDOWN,
  value: TPlayResults.TOUCHDOWN,
  isHorizontal: true,
  isToggle: true,
  toggleInitialValue: true,
};

export const TURNOVER_BTN: ISelectionButton = {
  color: TColors.RED,
  text: TPlayResults.TURNOVER,
  value: TPlayResults.TURNOVER,
  isHorizontal: true,
  isToggle: true,
  toggleInitialValue: true,
};

export const FIRST_DOWN_BTN: ISelectionButton = {
  color: TColors.ORANGE,
  text: TPlayResults.FIRST_DOWN,
  value: TPlayResults.FIRST_DOWN,
  isHorizontal: true,
  isToggle: true,
  toggleInitialValue: true,
};
